/**
 * Plain HTTP helpers for the page-break toggle flow inside
 * `StoryPreview.tsx`. These replace two compose-app effects
 * (`fetchStoryContent` and `updateSectionContentWithId`) that were
 * identical shape — a single Hasura POST with no state dispatches, no
 * selectors, and no error-toast side effects.
 *
 * Kept deliberately simple:
 *   - Plain async functions, not hooks. The caller is an event listener
 *     added imperatively to iframe sections — it cannot call hooks.
 *   - Mirror the exact request shape the old effects produced: same URL,
 *     same JSON body format, same expected response shape.
 *
 * This is the first slice of the compose-app → hooks migration. It is
 * intentionally the smallest possible change so the HTTP-layer swap can
 * be validated in isolation before we tackle state / route / view
 * migrations.
 */

import {
  fetchStoryContent as fetchStoryContentQuery,
  updateSectionContentWithId as updateSectionContentMutation
} from '../../../../graphQL/queries/publicationsQueries';
import { postHasuraGraphql } from '../../../../utils/hasuraGraphql';

type IFetchStoryContentData = {
  storyContent: { _id: string; content: string }[];
};

type IUpdateSectionContentData = {
  update_storyContents?: { affected_rows: number };
};

export type ISectionContentResult = {
  /** Hasura row ID — same shape the old effect returned. */
  id: string;
  content: string;
};

/**
 * Fetch a single `storyContent` row matching the given substring. Used by
 * the page-break toggle to locate the HTML for a specific section.
 *
 * Returns `{ id, content }` of the first matching row. Throws if no row
 * matches.
 */
export async function fetchSectionContent(params: {
  storyId: string;
  contentSubstring: string;
}): Promise<ISectionContentResult> {
  const data = await postHasuraGraphql<IFetchStoryContentData>(fetchStoryContentQuery, {
    storyId: params.storyId,
    contentSubstring: params.contentSubstring
  });

  const row = data.storyContent?.[0];
  if (!row) {
    throw new Error('No storyContent row matched the given substring');
  }
  return { id: row._id, content: row.content };
}

/**
 * Patch a `storyContents` row's HTML (used to toggle the page-break
 * class on a section).
 */
export async function updateSectionContent(params: {
  sectionId: string;
  updatedContent: string;
}): Promise<IUpdateSectionContentData> {
  return postHasuraGraphql<IUpdateSectionContentData>(updateSectionContentMutation, {
    id: params.sectionId,
    content: params.updatedContent,
    contentHtml: params.updatedContent
  });
}
